import { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WaitlistForm from "@/components/WaitlistForm";
import QuickCalculator from "@/components/QuickCalculator";
import SketchGallery from "@/components/SketchGallery";
import Testimonials from "@/components/Testimonials";
import { setPendingUpload } from "@/lib/pendingUpload";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, Home, Warehouse, Building2, Factory, PencilRuler, Calculator,
  FileText, Layers, ChevronRight, Box, Wine, Camera, Award, ChevronUp,
} from "lucide-react";

const IMG = {
  hero: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  home: "https://images.unsplash.com/photo-1730170787463-53a7074137fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  warehouse: "https://images.pexels.com/photos/4115457/pexels-photo-4115457.jpeg?auto=compress&cs=tinysrgb&w=800",
  site: "https://images.pexels.com/photos/4832564/pexels-photo-4832564.jpeg?auto=compress&cs=tinysrgb&w=1200",
};

const types = [
  { icon: Home, label: "Homes & Apartments", img: IMG.home },
  { icon: Warehouse, label: "Warehouses", img: IMG.warehouse },
  { icon: Building2, label: "Commercial & Office", img: IMG.site },
  { icon: Factory, label: "Industrial", img: IMG.warehouse },
];

const steps = [
  { icon: PencilRuler, title: "Design the layout", desc: "Configure type, size and rooms, or draw a floor plan on the interactive blueprint canvas." },
  { icon: Calculator, title: "Get AI cost scopes", desc: "Instant national-average estimates broken down by material and labor for every scope of work." },
  { icon: FileText, title: "Save & manage", desc: "Store every project in your customer portal, re-estimate, and compare builds anytime." },
];

export default function Landing() {
  const navigate = useNavigate();
  const napkinCamRef = useRef();
  const [digest, setDigest] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/forum/digest").then(({ data }) => setDigest(data.slice(0, 3))).catch(() => {});
    api.get("/stats/public").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const onNapkinSnap = (f) => {
    if (!f) return;
    setPendingUpload(f);
    navigate("/generate");
  };

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        <img src={IMG.hero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/70 to-background" />
        <div className="relative mx-auto max-w-7xl px-4 py-24 sm:px-8 lg:py-36">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            className="font-mono2 text-xs uppercase tracking-[0.3em] text-primary">
            Design · Estimate · Build
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
            className="mt-4 max-w-3xl font-heading text-5xl font-extrabold leading-none tracking-tighter sm:text-6xl lg:text-7xl">
            Build your blueprint. Know your cost.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.16 }}
            className="mt-6 max-w-xl text-lg text-muted-foreground">
            Costobuild turns your custom home, apartment or warehouse idea into a ready blueprint with a full
            scope of work — material costs and national-average labor pricing included.
          </motion.p>
          {stats && (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              data-testid="live-stats-ticker"
              className="mt-7 inline-flex items-center gap-2 border border-primary/40 bg-primary/10 px-4 py-2">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
              </span>
              <span className="font-mono2 text-xs uppercase tracking-widest text-foreground">
                <span className="font-bold text-primary" data-testid="stats-week-count">{stats.estimates_week.toLocaleString()}</span> estimates run this week across{" "}
                <span className="font-bold text-primary" data-testid="stats-country-count">{stats.countries}</span> countries
              </span>
            </motion.div>
          )}
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }}
            className="mt-9 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-start">
              <Button size="lg" className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                Start Designing <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/login" data-testid="hero-signin">
              <Button size="lg" variant="outline" className="rounded-sm border-border font-mono2 uppercase tracking-widest">
                Sign In
              </Button>
            </Link>
          </motion.div>

          <div className="mt-16 grid max-w-2xl grid-cols-3 border border-border">
            {[["4", "Build types"], ["10+", "Cost scopes"], ["AI", "Estimation"]].map(([v, l], i) => (
              <div key={i} className="border-r border-border p-5 last:border-r-0">
                <div className="font-mono2 text-3xl font-bold text-primary">{v}</div>
                <div className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Napkin → Home wedge */}
      <section className="relative overflow-hidden border-b border-border bg-[#050a16]" data-testid="napkin-wedge">
        <div className="absolute inset-0 bp-grid-fine opacity-40" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 py-20 sm:px-8 lg:grid-cols-2 lg:py-28">
          <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <p className="inline-flex items-center gap-2 border border-primary/40 px-3 py-1 font-mono2 text-xs uppercase tracking-[0.25em] text-primary">
              <Wine className="h-3.5 w-3.5" /> The napkin test
            </p>
            <h2 className="mt-6 font-heading text-4xl font-extrabold leading-[0.95] tracking-tighter sm:text-5xl lg:text-6xl">
              From a napkin<br />
              <span className="text-primary">in the bar</span><br />
              to a real home.
            </h2>
            <p className="mt-6 max-w-lg text-lg text-muted-foreground">
              That rough sketch you scribbled on a cocktail napkin? Snap a photo of it. Costobuild reads your
              idea, turns it into a build-ready blueprint and interactive 3D model, and hands you a full scope of
              work with material and labor costs — all before your next round.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <input ref={napkinCamRef} type="file" accept="image/*" capture="environment" hidden
                onChange={(e) => onNapkinSnap(e.target.files?.[0])} data-testid="napkin-camera-input" />
              <Button size="lg" onClick={() => napkinCamRef.current?.click()} data-testid="napkin-snap-btn"
                className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                <Camera className="mr-2 h-4 w-4" /> Snap Your Napkin
              </Button>
              <Link to="/generate" data-testid="napkin-cta">
                <Button size="lg" variant="outline" className="rounded-sm border-border font-mono2 uppercase tracking-widest">
                  <Box className="mr-2 h-4 w-4" /> Upload a Sketch
                </Button>
              </Link>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0, scale: 0.96 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
            transition={{ duration: 0.6 }} className="relative overflow-hidden border border-border">
            <img src="/napkin-to-home.jpg" alt="A bar napkin sketch transforming into a photorealistic custom home"
              className="h-full w-full object-cover" />
            <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-primary/20" />
          </motion.div>
        </div>
      </section>

      {/* Sketch gallery — before/after proof */}
      <section className="border-b border-border" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
          <div className="mb-10 max-w-2xl">
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent">Proof it works</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              From scribble to structure
            </h2>
            <p className="mt-3 text-muted-foreground">
              Real napkin sketches, turned into build-ready homes. Hover or tap any card to flip between the
              napkin and the finished build.
            </p>
          </div>
          <SketchGallery />
        </div>
      </section>

      {/* Testimonials */}
      <section className="border-b border-border bg-secondary/10">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
          <div className="mb-10 max-w-2xl">
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent">What people built from a napkin</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              Loved by builders worldwide
            </h2>
          </div>
          <Testimonials />
        </div>
      </section>

      {/* Community top answers */}
      {digest.length > 0 && (
        <section className="border-b border-border" data-testid="landing-digest">
          <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
            <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
              <div className="max-w-2xl">
                <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent"><Award className="mr-2 inline h-3.5 w-3.5" /> From the community</p>
                <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Top answers from real builders</h2>
              </div>
              <Link to="/forum"><Button variant="outline" className="rounded-sm border-border font-mono2 uppercase tracking-widest">Visit the forum <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            </div>
            <div className="grid gap-5 md:grid-cols-3">
              {digest.map((d, i) => (
                <Link key={i} to={`/forum/${d.thread_id}`} data-testid={`landing-digest-${i}`}
                  className="group flex flex-col border border-border bg-secondary/10 p-6 transition-colors hover:border-primary">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{d.category_label}</span>
                    <span className="flex items-center gap-2 font-mono2 text-[10px] uppercase tracking-widest">
                      {d.is_best && <span className="inline-flex items-center gap-1 text-primary"><Award className="h-3 w-3" /> Best</span>}
                      <span className="inline-flex items-center gap-0.5 text-muted-foreground"><ChevronUp className="h-3 w-3" />{d.upvotes}</span>
                    </span>
                  </div>
                  <h3 className="mt-2 font-heading text-base font-semibold group-hover:text-primary">{d.thread_title}</h3>
                  <p className="mt-2 flex-1 text-sm text-muted-foreground line-clamp-3">{d.snippet}</p>
                  <p className="mt-3 font-mono2 text-[10px] uppercase tracking-widest text-muted-foreground">{d.author_name}{d.author_vip && <span className="text-primary"> · VIP</span>}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Quick calculator */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
          <div className="mb-10 max-w-2xl">
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent">Try it now</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">
              What will your build cost?
            </h2>
            <p className="mt-3 text-muted-foreground">
              Get an instant national-average ballpark in seconds — no signup required. Ready for the full
              scope-by-scope AI breakdown? Create a free profile.
            </p>
          </div>
          <QuickCalculator />
        </div>
      </section>
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
        <div className="mb-12 flex items-end justify-between">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent">The workflow</p>
            <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Three steps to a build-ready plan</h2>
          </div>
        </div>
        <div className="grid border border-border md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="border-b border-r border-border p-8 last:border-r-0 md:border-b-0">
              <div className="mb-5 flex h-11 w-11 items-center justify-center border border-border bg-secondary/40">
                <s.icon className="h-5 w-5 text-primary" />
              </div>
              <span className="font-mono2 text-xs text-muted-foreground">0{i + 1}</span>
              <h3 className="mt-1 font-heading text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-muted-foreground">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3D generator highlight */}
      <section className="border-y border-border bg-secondary/10">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-20 sm:px-8 lg:grid-cols-2">
          <div>
            <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">New · AI Blueprint → 3D</p>
            <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-tighter sm:text-4xl lg:text-5xl">
              Upload a blueprint. Watch it become 3D.
            </h2>
            <p className="mt-4 max-w-lg text-muted-foreground">
              Drop in a floor plan, blueprint or rough sketch — our AI reads the layout, builds an interactive 3D
              model you can orbit, and renders a photorealistic vision of the finished building.
            </p>
            <Link to="/generate" className="mt-8 inline-block" data-testid="landing-generate-cta">
              <Button size="lg" className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                <Box className="mr-2 h-4 w-4" /> Try It Free
              </Button>
            </Link>
          </div>
          <div className="relative aspect-video overflow-hidden border border-border bg-[#050a16]">
            <img src={IMG.warehouse} alt="3D generation" className="h-full w-full object-cover opacity-60" />
            <div className="absolute inset-0 bp-grid-fine" />
            <div className="absolute bottom-4 left-4 flex items-center gap-2 border border-border bg-background/80 px-3 py-1.5 font-mono2 text-xs uppercase tracking-widest text-primary backdrop-blur">
              <Box className="h-4 w-4" /> Interactive 3D + Render
            </div>
          </div>
        </div>
      </section>

      {/* Build types */}
      <section className="border-y border-border bg-secondary/10">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-8">
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-accent">Any structure</p>
          <h2 className="mt-2 font-heading text-3xl font-bold tracking-tight sm:text-4xl">Built for every project</h2>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {types.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className="group relative aspect-[4/5] overflow-hidden border border-border">
                <img src={t.img} alt={t.label} className="h-full w-full object-cover opacity-40 transition-transform duration-500 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />
                <div className="absolute bottom-0 left-0 p-5">
                  <t.icon className="mb-2 h-6 w-6 text-primary" />
                  <p className="font-heading text-lg font-semibold">{t.label}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-24 sm:px-8">
        <div className="relative overflow-hidden border border-border p-10 sm:p-16">
          <div className="absolute inset-0 bp-grid-fine opacity-60" />
          <div className="relative">
            <Layers className="mb-4 h-8 w-8 text-primary" />
            <h2 className="max-w-2xl font-heading text-3xl font-extrabold tracking-tighter sm:text-5xl">
              Your next build starts with a number.
            </h2>
            <p className="mt-4 max-w-xl text-muted-foreground">
              Create your free profile and get an instant, contractor-grade estimate on your first design.
            </p>
            <div className="mt-8 flex flex-col gap-4">
              <Link to="/register" className="inline-block" data-testid="cta-start">
                <Button size="lg" className="rounded-sm bg-primary font-mono2 uppercase tracking-widest text-black hover:bg-primary/90">
                  Create Free Profile <ChevronRight className="ml-1 h-4 w-4" />
                </Button>
              </Link>
              <div>
                <p className="mb-2 font-mono2 text-xs uppercase tracking-widest text-muted-foreground">Or join the waitlist for updates</p>
                <WaitlistForm source="landing" testid="landing-waitlist" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
